/**
 * GP Capital — Insights content model.
 *
 * Canonical content store for the public /insights section.
 *
 * Source-of-truth for:
 *   - article metadata (slug, title, dek/excerpt, category, audience, read time, date, author)
 *   - per-article SEO (seoTitle, seoDescription)
 *   - article body (markdown, rendered through the SafeMarkdown component)
 *
 * Article author byline is the single string "GP Capital" across all articles.
 *
 * Every article body follows the same structure:
 *   Problem  ->  Why it happens  ->  A practical framework  ->  Where GP CAP fits  ->  CTA
 * The shared disclaimer is rendered automatically below every body.
 *
 * STRICTLY NO:
 *   - guaranteed funding / returns, high yield, APY, risk-free, best terms
 *   - "secure funding", "will fund", "approved finance", "get funded", "apply now"
 *   - investment / financial-product advice, retail investor, passive income
 *   - deal certainty, capital guaranteed, lender commitment
 *   - "GP CAP lends / invests / manages capital"
 *   - invented statistics, deal outcomes, lender names, borrower names, amounts,
 *     transaction examples, approval rates or market-share claims
 *   - AFSL / ACL / licensing claims
 *   - confidential data
 */

export const insightCategories = [
  { key: 'all',                       label: 'All' },
  { key: 'market-notes',              label: 'Market Notes' },
  { key: 'broker-education',          label: 'Broker Education' },
  { key: 'borrower-guidance',         label: 'Borrower Guidance' },
  { key: 'capital-partner-education', label: 'Capital Partner Education' },
  { key: 'capital-structuring',       label: 'Capital Structuring' },
  { key: 'platform-trust',            label: 'Platform & Trust' },
];

export const insightAudiences = [
  { key: 'lenders',   label: 'Lenders / Capital Partners' },
  { key: 'borrowers', label: 'Borrowers' },
  { key: 'all',       label: 'All' },
];

/**
 * Shared disclaimer rendered at the bottom of every article body.
 * Standard site disclaimer component — do not paraphrase.
 */
export const insightsDisclaimer =
  'GP CAP Pty Ltd does not lend money, manage investments, provide financial product advice or guarantee transaction outcomes. GP CAP supports selected capital introduction, transaction preparation and controlled distribution processes for suitable counterparties. This article is general information only and does not take account of any particular objectives or circumstances.';

/** Restrained CTA line appended to every article body. */
const CTA_LINE =
  'Contact GP CAP Pty Ltd to discuss whether a transaction may be suitable for review.';

export const insights = [
  {
    slug: 'the-first-review-why-private-credit-deals-lose-momentum-early',
    title: 'The first review: why private credit deals lose momentum early',
    dek: "Most transactions that stall don't fail on substance. They lose momentum in the first review, before anyone has really looked. Here is why.",
    category: 'capital-structuring',
    audience: 'all',
    readTime: '5 min read',
    date: '2026-07-06',
    author: 'GP Capital',
    seoTitle: 'Private Credit Deals: The First Review | GP Capital',
    seoDescription:
      'Why many private credit transactions lose momentum at the first review, and what preparation keeps a deal moving through early assessment.',
    body: `A transaction rarely dies with a firm "no". More often it simply slows — the reviewer moves on to something clearer, replies get shorter, and the opportunity quietly loses momentum in the first review. The substance may be sound; the problem is that it never got a fair look.

## Why it happens

The first review is fast and unforgiving. A reviewer forms an early view from what they can see immediately, and an early view is hard to reverse. When the basics are missing or scattered — an unclear purpose, an undefined security position, no valuation basis, no exit — the reviewer cannot build a picture, so caution rises and attention drifts. Nothing is rejected; it is just deprioritised. And a deprioritised transaction is very difficult to revive, because the second look never comes.

## A practical framework

Keeping momentum through the first review is mostly about removing reasons to pause:

**Lead with the essentials.** Purpose, amount, structure, security position and exit, stated plainly at the top.

**Make the position explicit.** The asset, its proposed rank, and anything ahead of the request — so the reviewer is not left inferring.

**Support the numbers.** A valuation basis and a defined repayment path, rather than figures presented without context.

**Organise the evidence.** Core documents in one place, current and easy to navigate.

**Remove the obvious questions.** Every gap the reviewer has to chase is a chance for the transaction to stall.

Momentum is not built by pressure. It is built by leaving nothing that forces the reviewer to stop and wait.

## Where GP CAP fits

GP CAP's preparation and review process is aimed at exactly this early stage. A transaction is standardised and checked for completeness before it reaches a capital partner, so the first review is spent forming a view rather than assembling one. It does not lend, invest or decide outcomes — it works to ensure a well-prepared transaction is not lost simply because it was hard to read.

${CTA_LINE}`,
  },
  {
    slug: 'the-valuation-gap-current-completion-value-and-loan-amount',
    title: "The valuation gap: when current value, completion value and loan amount don't align",
    dek: 'Three numbers, one common source of stalled transactions. Understanding how current value, completion value and the loan amount interact is essential to a clean review.',
    category: 'capital-structuring',
    audience: 'all',
    readTime: '6 min read',
    date: '2026-07-03',
    author: 'GP Capital',
    seoTitle: 'The valuation gap in real estate finance explained | GP Capital',
    seoDescription:
      'How current value, completion value and the loan amount interact in real estate finance, and why a mismatch between them shapes how a transaction is assessed.',
    body: `In real estate finance, three figures do a lot of work: what the asset is worth today, what it is expected to be worth on completion, and how much capital is being requested. When those three do not line up sensibly, a "valuation gap" opens — and it is one of the most common reasons a transaction becomes harder to assess.

## Why it happens

The three numbers describe different moments and carry different certainty. Current value is grounded but may be modest for a site mid-project. Completion value is larger but depends on assumptions — cost, timing, market, execution — that have not yet been proven. The loan amount is a present request against a future outcome. Trouble arises when the request leans heavily on completion value while the current value and the assumptions behind completion are treated as settled facts. The gap between "worth now" and "worth later" is where risk lives.

## A practical framework

A clearer presentation separates the three and shows the bridge between them:

**State each value distinctly.** Current value, completion value, and the basis and date of each — not a single blended figure.

**Show leverage against both.** Where the request sits relative to current value and relative to completion value, so the reviewer sees both angles.

**Expose the assumptions.** What has to happen for completion value to be realised, and what is still unproven.

**Address the interim.** How the position looks before completion, not only after.

**Define the exit against the right number.** Repayment tends to rely on completion or refinance value; that dependency should be explicit.

A valuation gap is not fatal. An unexplained one usually is.

## Where GP CAP fits

GP CAP standardises how these figures are presented, so current value, completion value and the request are shown clearly and consistently rather than blended into a single optimistic number. It does not set values or guarantee outcomes — it helps a transaction present its valuation position honestly, so capital partners can assess the gap rather than be surprised by it.

${CTA_LINE}`,
  },
  {
    slug: 'presales-unsold-stock-and-refinancing-risk-in-development-finance',
    title: 'Presales, unsold stock and refinancing risk in development finance',
    dek: 'Presales coverage and unsold stock are not just sales metrics. They shape the refinance, and capital partners read them closely.',
    category: 'capital-structuring',
    audience: 'all',
    readTime: '6 min read',
    date: '2026-07-01',
    author: 'GP Capital',
    seoTitle: 'Presales & Development Refinancing Risk | GP Capital',
    seoDescription:
      'How presales coverage and unsold stock influence refinancing risk in development finance, and what capital partners typically consider.',
    body: `In development finance, presales and unsold stock are often treated as marketing progress. To a capital partner they are something more specific: leading indicators of how, and whether, the facility gets repaid. The refinance or takeout usually depends on them.

## Why it happens

A development facility is repaid at the end — through sales, a refinance, or both. Presales give early evidence of demand and underpin part of that repayment; unsold stock at completion is exposure that still has to be cleared or refinanced. When presales are thin or heavily conditional, or when a large volume of stock is expected to remain unsold at completion, the exit becomes less certain and the refinance harder to size. The risk is not the construction itself; it is what happens after it.

## A practical framework

A clearer picture of refinancing risk addresses:

**Presales quality, not just quantity.** How many, how firm, and how conditional — genuine, unconditional contracts read very differently from soft expressions of interest.

**Unsold stock at completion.** The expected residual, and the plan for it — sell down, hold, or refinance.

**The takeout path.** Whether repayment relies on sales, a refinance, or a combination, and how realistic each is.

**Timing.** How the sales and settlement timeline lines up with the facility term.

**Sensitivity.** What happens to the exit if sales are slower or the residual larger than expected.

Presales and unsold stock are, in effect, the transaction's answer to "how does this get repaid?"

## Where GP CAP fits

GP CAP helps a development transaction present these factors clearly and consistently, so the takeout logic is visible rather than assumed. It does not lend, guarantee sales or promise outcomes — it supports a presentation in which capital partners can weigh refinancing risk on organised, current information.

${CTA_LINE}`,
  },
  {
    slug: 'cost-escalation-the-quiet-risk-in-construction-finance',
    title: 'Cost escalation: the quiet risk in construction finance',
    dek: 'It rarely leads the headline summary, but cost escalation often decides the outcome. A short note on the risk that hides in plain sight.',
    category: 'capital-structuring',
    audience: 'all',
    readTime: '5 min read',
    date: '2026-06-27',
    author: 'GP Capital',
    seoTitle: 'Cost escalation risk in construction finance | GP Capital',
    seoDescription:
      'Why cost escalation is a quiet but material risk in construction finance, and how it is considered during transaction review.',
    body: `Construction finance summaries tend to lead with the headline: the site, the end value, the projected margin. Cost escalation — the risk that the build costs more than budgeted — sits quietly beneath all of it, and it is often what turns a workable transaction into a strained one.

## Why it happens

A construction budget is a set of estimates made before the work is done. Materials, labour, subcontractor availability, delays and variations all move, and they tend to move in one direction. When the contingency is thin, the program optimistic, or the contract structure exposed, even a modest overrun can erode the margin that made the transaction viable. Because escalation appears gradually rather than all at once, it is easy to under-weight at the start — which is exactly why reviewers look for it.

## A practical framework

Addressing cost escalation credibly means showing:

**Contingency.** How much is built in, and whether it is realistic for the type of build.

**Contract structure.** Fixed-price, cost-plus or hybrid, and who carries the risk of overruns.

**Program.** Whether the timeline is achievable, since delay and cost tend to travel together.

**Builder capacity.** Whether the builder is resourced to deliver without strain.

**Headroom.** What the margin looks like if costs rise, not only if they hold.

A transaction that has already stress-tested its costs is far more credible than one that assumes the budget holds exactly.

## Where GP CAP fits

GP CAP helps a construction transaction present its cost and contingency position clearly, so escalation risk is visible and can be assessed rather than discovered later. It does not build, price works or guarantee outcomes — it supports a presentation in which capital partners can weigh the quiet risks alongside the headline numbers.

${CTA_LINE}`,
  },
  {
    slug: 'why-lender-fit-matters-more-than-lender-count',
    title: 'Why lender fit matters more than lender count',
    dek: 'Reaching twenty capital partners is not twenty chances. Fit — not count — determines the quality of the review a transaction receives.',
    category: 'borrower-guidance',
    audience: 'borrowers',
    readTime: '5 min read',
    date: '2026-06-24',
    author: 'GP Capital',
    seoTitle: 'Why lender fit matters more than lender count | GP Capital',
    seoDescription:
      'Why reaching the right capital partners matters more than reaching many, and how fit shapes the quality and pace of a review.',
    body: `When raising capital, it is tempting to measure progress by reach: how many capital partners have seen the transaction. But a transaction sent to twenty parties is not twenty opportunities. If nineteen of them were never a fit, the effort mostly generates noise — and can quietly damage the deal.

## Why it happens

Reach is easy to count and feels like momentum, so it gets mistaken for progress. But capital partners have mandates: asset types, structures, positions and sizes they consider, and plenty they do not. Sending a transaction to parties outside their mandate does not create a chance; it creates a decline, and often a sense that the deal is being shopped widely. Volume without fit tends to lower the quality of engagement rather than raise the odds.

## A practical framework

Prioritising fit over count means asking, before an approach:

**Mandate.** Does this capital partner actually consider transactions of this type, structure and size?

**Position.** Does the security position on offer match what they take?

**Stage and risk.** Does the profile — development, bridging, refinance, special situation — sit within their appetite?

**Relationship and record.** Is there a basis for a serious, considered review rather than a quick pass?

A shortlist of genuinely relevant capital partners will almost always produce better engagement than a broadcast to many.

## Where GP CAP fits

GP CAP's model is built on matched, controlled distribution: a transaction is opened to the capital partners for whom it is genuinely a fit, rather than circulated to a list. It does not lend or guarantee outcomes — it works to ensure that the parties who see a transaction are the ones most likely to review it seriously.

${CTA_LINE}`,
  },
  {
    slug: 'the-security-position-what-lenders-need-before-engaging',
    title: 'The security position: what lenders need to understand before engaging',
    dek: 'Before a capital partner engages, one question sits above the rest: what secures this, and in what position? Here is what a clear answer looks like.',
    category: 'capital-partner-education',
    audience: 'lenders',
    readTime: '5 min read',
    date: '2026-06-20',
    author: 'GP Capital',
    seoTitle: 'The security position: what lenders need before engaging | GP Capital',
    seoDescription:
      'Why the security position — asset, rank and anything ahead of the request — is one of the first things a capital partner needs to understand.',
    body: `Much of a capital partner's early assessment turns on one thing: the security position. Before committing time to a transaction, a reviewer needs to understand what stands behind the request and where they would sit relative to others. An unclear security position stops engagement before it starts.

## Why it happens

Security is the reviewer's protection, so it is assessed first. Yet it is often presented vaguely — an asset named without its position, a request described without reference to what ranks ahead of it. When the reviewer cannot quickly establish rank, leverage and priority, they cannot gauge their downside, and without a view of the downside there is little basis to engage. Ambiguity here is read as risk.

## A practical framework

A clear security position sets out:

**The asset.** What is offered, its type and general location.

**The rank.** First, second or another position, stated plainly.

**What sits ahead.** Any existing facility or claim with priority, and its scale relative to the asset.

**Leverage.** Where the request sits against value — and, for development, against both current and completion value.

**Enforceability.** Whether the security is clean and the path to it is straightforward.

The goal is that a reviewer can establish, in a first pass, exactly where they would stand.

## Where GP CAP fits

GP CAP standardises how the security position is presented, so rank, priority and leverage are explicit rather than implied. It does not lend, take security or guarantee outcomes — it supports a presentation in which a capital partner can understand their position clearly enough to decide whether to engage.

${CTA_LINE}`,
  },
  {
    slug: 'development-sites-with-planning-risk-what-capital-partners-ask-first',
    title: 'Development sites with planning risk: what capital partners usually ask first',
    dek: 'When planning risk is in play, capital partners tend to ask a predictable set of questions first. Knowing them helps a transaction respond well.',
    category: 'capital-partner-education',
    audience: 'all',
    readTime: '5 min read',
    date: '2026-06-17',
    author: 'GP Capital',
    seoTitle: 'Development Planning Risk: Lender Questions | GP Capital',
    seoDescription:
      'The questions capital partners typically ask first when a development site carries planning or approval risk, and how to present them clearly.',
    body: `A development site whose value depends on a planning outcome carries a specific kind of uncertainty. Capital partners know this, and when planning risk is present they tend to open with a consistent set of questions. Anticipating them makes the difference between a transaction that reads as considered and one that reads as speculative.

## Why it happens

Planning risk sits between what a site is worth today and what it could be worth if approvals are granted. Because that difference can be large, the questions cluster around how likely, how long and how conditional the approval path is. A reviewer is not looking for certainty — planning is rarely certain — but for a realistic, evidenced view of the risk, rather than an assumption that approval is a formality.

## A practical framework

The first questions usually cover:

**Current status.** What approvals exist now, and what is still required.

**The gap.** What the site is worth as-is versus with the intended approval.

**Likelihood.** The basis for confidence in the planning outcome — precedent, advice, engagement with authorities.

**Timeline.** How long the process realistically takes, and how that fits the facility term.

**Fallback.** What the position looks like if the approval is delayed, reduced or refused.

A transaction that answers these directly treats planning risk as a known variable rather than an article of faith.

## Where GP CAP fits

GP CAP helps a transaction present its planning position and the evidence behind it clearly, so capital partners can assess the risk on organised information. It does not obtain approvals, value sites or guarantee outcomes — it supports a presentation in which planning risk is visible and can be weighed properly.

${CTA_LINE}`,
  },
  {
    slug: 'when-a-refinance-becomes-a-special-situation',
    title: 'When a refinance becomes a special situation',
    dek: 'Not every refinance is routine. Certain signals move a transaction into special-situation territory, and the review changes with them.',
    category: 'capital-structuring',
    audience: 'all',
    readTime: '5 min read',
    date: '2026-06-13',
    author: 'GP Capital',
    seoTitle: 'When a refinance becomes a special situation | GP Capital',
    seoDescription:
      'The signals that turn a straightforward refinance into a special-situation transaction, and how the review changes as a result.',
    body: `Most refinances are ordinary: an existing facility is replaced with a new one on similar or better terms. But some carry features that move them out of the routine category and into what capital partners treat as a special situation — reviewed differently, and by different parties.

## Why it happens

A refinance becomes a special situation when the reason for it is not simply "the term is ending". Time pressure from a maturing or defaulted facility, a valuation that has moved against the borrower, a broken original exit, or complexity in the existing capital stack all change the nature of the request. The transaction is no longer just about pricing a clean asset; it is about resolving a situation. Reviewers who handle standard refinances may not handle these, and vice versa — so recognising the category early matters.

## A practical framework

Signals that a refinance is really a special situation include:

**Urgency.** A near-term maturity, expiry or enforcement pressure driving the timeline.

**Value movement.** A current position that has shifted materially since the original facility.

**A broken exit.** The original repayment path no longer available as planned.

**Stack complexity.** Multiple facilities, positions or parties that must be resolved together.

**Story required.** A transaction that needs explanation, not just numbers, to be understood.

Naming the situation honestly leads to the right capital partners; disguising it as a clean refinance usually wastes everyone's time.

## Where GP CAP fits

GP CAP helps a transaction be presented for what it is, and matched to capital partners who consider that profile — rather than circulated as routine to parties who do not. It does not lend, resolve situations or guarantee outcomes — it supports a clear presentation and controlled distribution to relevant counterparties.

${CTA_LINE}`,
  },
  {
    slug: 'capital-stack-misalignment-senior-debt-equity-problem',
    title: 'Capital stack misalignment: when senior debt is asked to solve an equity problem',
    dek: 'When senior debt is asked to fill what is really an equity gap, the capital stack is already misaligned. A short note on why it shows up in review.',
    category: 'capital-structuring',
    audience: 'all',
    readTime: '5 min read',
    date: '2026-06-10',
    author: 'GP Capital',
    seoTitle: 'Capital Stack Misalignment: Debt & Equity | GP Capital',
    seoDescription:
      'Why asking senior debt to solve what is really an equity gap creates capital stack misalignment, and how it surfaces during transaction review.',
    body: `A capital stack works when each layer does the job it is priced for: senior debt takes the safest position, equity absorbs the first risk, and anything between is priced accordingly. Misalignment happens when one layer is asked to do another's job — most commonly, when senior debt is stretched to cover what is really an equity shortfall.

## Why it happens

Equity is the most expensive and hardest capital to raise, so there is a natural pull to substitute cheaper debt for it. When a project is short on equity, the request to senior debt quietly creeps up to fill the gap. But senior debt is priced and positioned for low risk; asked to sit where equity should be, it is carrying risk it is not structured for. A reviewer sees this quickly, because the leverage and the position no longer match the pricing being sought.

## A practical framework

Signs of a misaligned stack — and how to address them — include:

**Leverage that implies thin equity.** A senior request so high that little genuine equity sits beneath it.

**Position vs pricing.** Senior terms sought for what is effectively a higher-risk position.

**Unclear equity source.** Equity assumed or deferred rather than committed.

**Mislabelled layers.** Capital described as one type but behaving as another.

The fix is usually structural: right-size the senior request, identify genuine equity or a mezzanine layer, and align each layer with its role — rather than asking senior debt to absorb an equity problem.

## Where GP CAP fits

GP CAP helps a transaction present its capital stack clearly, so the role and risk of each layer are visible and can be matched to the right capital partners. It does not lend, provide equity or guarantee outcomes — it supports a presentation in which structural misalignment is apparent early and can be addressed.

${CTA_LINE}`,
  },
  {
    slug: 'ai-in-private-capital-better-information-flow-not-automated-judgment',
    title: 'AI in private capital: better information flow, not automated judgment',
    dek: "AI's real value in private capital is not deciding deals. It is making information flow better, so people can exercise judgment well.",
    category: 'platform-trust',
    audience: 'all',
    readTime: '6 min read',
    date: '2026-06-06',
    author: 'GP Capital',
    seoTitle: 'AI in Private Capital: Human-Led Judgment | GP Capital',
    seoDescription:
      'Where AI genuinely helps in private capital — organising and surfacing information and supporting workflow discipline — and why credit judgment stays with people.',
    body: `Discussion of AI in finance often jumps to automation: models that decide, approve or replace the reviewer. In private capital, that framing misses where the real, durable value sits. AI's contribution here is to information flow — organising, surfacing and structuring — not to judgment, which remains a human responsibility.

## Why it happens

Private capital runs on nuance: security positions, exits, sponsor context, structure and the particulars of a single asset. These are matters of judgment, formed by experienced people weighing incomplete information. What slows that judgment is rarely the thinking itself; it is the state of the information — scattered documents, inconsistent versions, and time lost assembling a picture before any assessment can begin. That is a workflow problem, and it is where tooling genuinely helps.

## A practical framework

Used well, AI supports the work without pretending to do it:

**Organisation.** Bringing documents into a single, navigable, current set.

**Retrieval.** Helping a reviewer find the relevant material quickly rather than searching manually.

**Consistency.** Presenting transactions in a standard structure, so like is compared with like.

**Workflow discipline.** Tracking what was shared, with whom and when, and flagging gaps to be addressed by a person.

What it should not do is decide. It does not make credit decisions, approve transactions, price risk or replace the reviewer's judgment. The value is a cleaner, faster path to a human view — not a substitute for one.

## Where GP CAP fits

GP CAP applies technology in exactly this spirit: to strengthen information flow, document organisation and workflow discipline, so capital partners spend their time on judgment rather than on assembling the picture. GP CAP does not lend, invest, make automated credit decisions or guarantee outcomes. Judgment stays with people; the platform simply makes the information they rely on cleaner and more controlled.

${CTA_LINE}`,
  },
  {
    slug: 'what-is-a-private-capital-introduction-platform',
    title: 'What is a private capital introduction platform?',
    dek: 'The term is used loosely. Here is a plain explanation of what a capital introduction platform does — and, just as importantly, what it does not do.',
    category: 'market-notes',
    audience: 'all',
    readTime: '5 min read',
    date: '2026-04-03',
    author: 'GP Capital',
    seoTitle: 'What is a private capital introduction platform? | GP Capital',
    seoDescription:
      'A plain explanation of what a private capital introduction platform does in real estate and private credit — capital introduction, transaction preparation and controlled distribution.',
    body: `"Capital introduction platform" is used to describe very different things, from broad listing sites to informal broker networks. For borrowers, brokers and capital partners deciding how to run a transaction, that ambiguity is a problem: the label alone tells you little about how information is handled or how counterparties are matched.

## Why it happens

The private capital market is relationship-driven and lightly standardised. Many introductions still happen over email, phone and forwarded documents, so anything with a website and a contact form can call itself a "platform". Because there is no common definition, the word absorbs whatever the operator wants it to mean, and the differences that actually matter — control, structure and suitability — get lost.

## A practical framework

A useful way to assess any introduction platform is to ask four questions:

**How is a transaction prepared?** Is an opportunity standardised into a consistent summary with an organised document set, or is it passed along in whatever form it arrived?

**How is information controlled?** Is confidential material gated by agreement and role, or shared openly once an enquiry is made?

**How are counterparties matched?** Is an opportunity opened to relevant capital partners on a suitable basis, or broadcast to a list?

**What record exists?** Is there a clear account of what was shared, with whom, and when?

A platform-led review process answers all four deliberately. An informal channel usually answers none of them.

## Where GP CAP fits

GP CAP is a private capital introduction platform in the deliberate sense: it supports transaction preparation, controlled distribution and a structured review process for suitable counterparties. It does not lend, invest or manage capital. Its role is to help a well-prepared opportunity reach relevant capital partners through a controlled process — nothing more, and nothing less.

${CTA_LINE}`,
  },
  {
    slug: 'why-controlled-distribution-matters-in-real-estate-finance',
    title: 'Why controlled distribution matters in real estate finance',
    dek: 'Sending a deal to everyone feels like maximising options. In practice it erodes the transaction. Here is why disciplined distribution serves both sides.',
    category: 'market-notes',
    audience: 'all',
    readTime: '5 min read',
    date: '2026-04-10',
    author: 'GP Capital',
    seoTitle: 'Controlled Distribution in Real Estate Finance | GP Capital',
    seoDescription:
      'Why controlled, permissioned distribution of a real estate finance opportunity protects borrowers and gives capital partners cleaner, more serious deal flow.',
    body: `A common instinct when raising capital is to circulate an opportunity as widely as possible. The logic seems sound: more eyes, more chances. In real estate finance the opposite is usually true — broad, uncontrolled distribution tends to weaken a transaction rather than strengthen it.

## Why it happens

Wide circulation is easy and feels productive. But once an opportunity has been forwarded across many inboxes, several things follow. Capital partners recognise a deal they have already seen elsewhere and assume it is being shopped. Confidential borrower and asset information travels beyond anyone's control. Different parties end up working from different versions. The transaction starts to look tired before it has been properly reviewed.

## A practical framework

Controlled distribution rests on a few principles:

**Match before you circulate.** Identify the capital partners for whom the opportunity is genuinely a fit, rather than sending it to a list.

**Gate confidential detail.** Share sensitive material only under appropriate agreement and only with relevant counterparties.

**Keep one current version.** A single, organised source of truth prevents the confusion of competing document chains.

**Maintain a record.** Knowing what was shared, with whom, and when protects everyone if questions arise later.

Distribution discipline is not about limiting interest. It is about presenting a serious transaction seriously.

## Where GP CAP fits

GP CAP applies controlled distribution as standard. An opportunity is prepared, then opened only to relevant capital partners under permissioned access. For the borrower, that protects confidentiality and market position. For the capital partner, it means the deal flow they see is organised and has not been broadcast. The transaction moves with less friction and a clear audit trail behind it.

${CTA_LINE}`,
  },
  {
    slug: 'how-gp-cap-reviews-a-real-estate-finance-opportunity',
    title: 'How GP CAP reviews a real estate finance opportunity',
    dek: 'Before anything is circulated, a transaction moves through a consistent review. Here is what that review looks at — and why it exists.',
    category: 'capital-structuring',
    audience: 'all',
    readTime: '5 min read',
    date: '2026-04-17',
    author: 'GP Capital',
    seoTitle: 'How GP CAP reviews a real estate finance opportunity | GP Capital',
    seoDescription:
      'An overview of the structured review a real estate finance opportunity moves through at GP CAP before controlled distribution to relevant capital partners.',
    body: `Not every opportunity that arrives is ready to be shown to a capital partner. A structured review sits between intake and distribution, and its purpose is simple: to make sure that when an opportunity is circulated, the obvious questions have already been answered.

## Why it happens

Opportunities arrive in very different states. Some are well organised; many are not. Without a consistent review, the gaps only surface later — when a capital partner asks for the valuation basis, the security position or the exit, and the answers are not there. Every such gap slows the transaction and reduces confidence. A review done up front is cheaper than one done in front of a reviewer.

## A practical framework

A structured review typically looks at:

**Coherence.** Do the purpose, amount, structure and security fit together and make sense as a transaction?

**Completeness.** Are the core materials present — ownership, financials, valuation status, planning or approvals where relevant, and the supporting documents?

**Clarity of position.** Is the security rank and the place in the capital stack explicit, including any facility ahead of the request?

**Credibility of exit.** Is there a defined, plausible repayment path?

**Suitability.** Is this the kind of opportunity that relevant capital partners would consider, presented in a way they can assess?

An opportunity that clears this review is not "approved" — it is simply ready to be reviewed by others on a serious footing.

## Where GP CAP fits

This review is the discipline GP CAP applies before controlled distribution. It does not lend, invest or decide outcomes; it prepares a transaction so that suitable counterparties can form their own view efficiently. The result is a cleaner process for the borrower and a more serious starting point for the capital partner.

${CTA_LINE}`,
  },
  {
    slug: 'difference-between-a-circulated-deal-and-a-fundable-transaction',
    title: 'The difference between a circulated deal and a fundable transaction',
    dek: 'Plenty of deals get circulated. Far fewer are genuinely ready for a capital partner to act on. The gap between the two is where most transactions stall.',
    category: 'capital-structuring',
    audience: 'all',
    readTime: '5 min read',
    date: '2026-04-24',
    author: 'GP Capital',
    seoTitle: 'Circulated deal vs fundable transaction: the difference | GP Capital',
    seoDescription:
      'Why a widely circulated deal is not the same as a transaction that is genuinely ready for capital-partner review, and what closes the gap between the two.',
    body: `A deal that is being circulated and a transaction that a capital partner can seriously assess are not the same thing. The distinction sounds obvious, yet it is where a large share of opportunities lose momentum: they are in motion, but they are not ready.

## Why it happens

Circulation is easy to start and hard to reverse. An opportunity can be in front of many parties while still missing the pieces a reviewer needs — a current valuation basis, a clear security position, a defined exit, organised documents. Movement gets mistaken for readiness. By the time the gaps surface, the deal has already formed a first impression, and first impressions in private capital are difficult to reset.

## A practical framework

A transaction is closer to reviewable when it can answer, without chasing:

**What is the capital for, and how much?** A precise purpose and quantum, not a range and a hope.

**What secures it, and in what position?** The asset, its location, the proposed rank, and anything ahead of it.

**What is it worth, and on what basis?** A supportable valuation position rather than an unsupported figure.

**How and when is it repaid?** A credible, defined exit.

**Is the paperwork ready?** Ownership, financials, approvals and asset-specific documents, organised in one place.

Circulation answers "who has seen it". Readiness answers "can it be acted on". Only the second moves a transaction forward.

## Where GP CAP fits

GP CAP's preparation and review process is aimed squarely at this gap. Rather than putting an opportunity in motion early, it works to make the transaction reviewable first, then distributes it in a controlled way to relevant capital partners. Readiness, not reach, is what earns a serious response.

${CTA_LINE}`,
  },
  {
    slug: 'how-brokers-protect-the-client-relationship-when-introducing-a-deal',
    title: 'How brokers can protect their client relationship when introducing a deal',
    dek: 'A broker\u2019s relationship with their client is the asset. Introducing a transaction should strengthen it, not put it at risk. Here is how to keep it protected.',
    category: 'broker-education',
    audience: 'all',
    readTime: '5 min read',
    date: '2026-05-01',
    author: 'GP Capital',
    seoTitle: 'Broker Introductions & Client Relationships | GP Capital',
    seoDescription:
      'Practical steps brokers can take to protect their client relationship and confidential information when introducing a private credit or real estate finance transaction.',
    body: `For a broker, the client relationship is the whole business. Introducing a transaction to capital should reinforce that relationship — yet a poorly controlled introduction can do the opposite, exposing the client's information and the broker's position at the same time.

## Why it happens

Introductions often rely on forwarding. A broker sends details to one contact, who sends them to another, and control is lost at the first hop. Once information is circulating freely, the broker can no longer see who holds it, the client's confidentiality is at risk, and the broker's role in the transaction becomes harder to protect. What began as a helpful introduction can quietly erode trust.

## A practical framework

Brokers can protect the relationship by insisting on a few things:

**Control the information.** Confidential detail should move under agreement and to relevant counterparties only — not by open forwarding.

**Keep the role visible.** The introduction and the broker's part in it should be recorded, not lost in a chain of emails.

**Present a prepared transaction.** A clean, organised pack reflects well on the broker and earns a more serious response.

**Set expectations with the client.** Explain how the process works, so the client sees the discipline behind the introduction.

Protecting the relationship is largely about protecting control of the information that sits inside it.

## Where GP CAP fits

GP CAP's controlled distribution supports the broker's position directly. Confidential material is gated rather than forwarded, access is permissioned and matched, and there is a clear record of the process. The broker introduces a prepared transaction through a controlled channel — and the client relationship stays where it belongs.

${CTA_LINE}`,
  },
  {
    slug: 'what-brokers-should-prepare-before-submitting-a-private-credit-transaction',
    title: 'What brokers should prepare before submitting a private credit transaction',
    dek: 'A well-prepared submission is engaged with quickly and seriously. Here is the groundwork worth doing before a private credit transaction goes forward.',
    category: 'broker-education',
    audience: 'all',
    readTime: '5 min read',
    date: '2026-05-08',
    author: 'GP Capital',
    seoTitle: 'Broker Checklist for Private Credit Submissions | GP Capital',
    seoDescription:
      'A practical preparation checklist for brokers before submitting a private credit or real estate finance transaction for controlled distribution.',
    body: `The quality of a submission shapes the response to it. A broker who submits a clear, complete transaction is engaged with quickly; a thin submission tends to stall while questions are chased. The groundwork done before submitting is rarely wasted.

## Why it happens

Under time pressure, it is tempting to submit what is at hand and fill in the rest later. But a reviewer forms a first view from what they can see now, not from what is promised to follow. Missing basics — purpose, security position, valuation status, exit — read as an unready transaction, regardless of the underlying merit.

## A practical framework

Before submitting, a broker is on stronger ground with:

**Purpose and use of funds.** Stated precisely, not as a general category.

**Amount, structure and term.** The quantum, the facility type, and where it sits in the capital stack.

**Security and position.** The asset, its location, the proposed rank, and any facility ahead of the request.

**Valuation status.** Whether a current valuation exists, who prepared it and on what basis.

**Planning and approvals.** For development or value-add, the current status and outstanding conditions.

**Borrower and sponsor background.** Who is behind the transaction and their relevant experience.

**Exit and timeline.** A defined repayment path and a realistic funding date with a genuine driver.

**Core documents.** Ownership, financials, plans or approvals, and asset-specific materials, organised in one place.

A submission that answers these signals that the rest of the transaction will be handled the same way.

## Where GP CAP fits

GP CAP standardises a submission into a consistent summary with an organised data room, identifies gaps early, and then distributes the prepared transaction under controlled access to relevant capital partners. The broker's preparation carries directly into a more serious, faster review.

${CTA_LINE}`,
  },
  {
    slug: 'what-lenders-look-for-before-reviewing-a-real-estate-debt-opportunity',
    title: 'What lenders look for before reviewing a real estate debt opportunity',
    dek: 'Capital partners triage quickly. Understanding what they check first helps a transaction earn a serious, timely read.',
    category: 'capital-partner-education',
    audience: 'lenders',
    readTime: '5 min read',
    date: '2026-05-15',
    author: 'GP Capital',
    seoTitle: 'Real Estate Debt: What Lenders Review | GP Capital',
    seoDescription:
      'The information experienced capital partners look for first when deciding whether to review a real estate debt or private credit opportunity.',
    body: `Capital partners reviewing several opportunities at once form a first view in minutes. Before committing real time to a real estate debt opportunity, most run a quick check to decide whether it is worth a closer look. Knowing what that check covers helps a transaction pass it.

## Why it happens

Attention is the scarce resource. A reviewer cannot study every opportunity in depth, so a fast triage protects their time. An incomplete or disorganised pack does not earn a second pass — not because the deal lacks merit, but because the reviewer cannot see the merit quickly enough to justify the effort.

## A practical framework

Before a detailed review, a capital partner typically looks for:

**Fit with mandate.** Does the amount, structure and asset type sit within what they consider?

**Clear security and position.** The asset, its location, the proposed rank, and anything ahead of it.

**Valuation basis.** Whether a current, supportable valuation position exists.

**Leverage against value or cost.** Where the request sits relative to the asset.

**A credible exit.** A defined repayment path — sale, refinance or completion.

**Sponsor background.** Who is behind the transaction and their relevant experience.

**Organised documents.** Materials that are current, complete and easy to navigate.

None of this is unusual; it is simply the information a reviewer needs to move from "maybe" to "worth my time".

## Where GP CAP fits

GP CAP prepares each opportunity so these questions are answered before it reaches a capital partner, and presents it through controlled, permissioned access. That does not change the reviewer's judgement — it simply lets them apply it to clean, current information rather than to a partial picture.

${CTA_LINE}`,
  },
  {
    slug: 'why-documentation-quality-matters-in-private-credit-execution',
    title: 'Why documentation quality matters in private credit execution',
    dek: 'Documentation discipline is not administrative box-ticking. In private credit, the quality of the paperwork often decides how fast — and how far — a transaction moves.',
    category: 'borrower-guidance',
    audience: 'borrowers',
    readTime: '5 min read',
    date: '2026-05-22',
    author: 'GP Capital',
    seoTitle: 'Documentation Quality in Private Credit | GP Capital',
    seoDescription:
      'How documentation discipline affects the pace and credibility of a private credit transaction, and what borrowers can do to strengthen it.',
    body: `Documentation is easy to treat as an afterthought — something to tidy up once the terms are agreed. In private credit execution, that order is backwards. The quality and organisation of the paperwork often shapes the transaction long before terms are on the table.

## Why it happens

A transaction is assessed through its documents. When materials are incomplete, out of date or scattered, a reviewer cannot verify what they are told, so caution rises and pace falls. Missing or inconsistent paperwork also signals how the rest of the transaction may be run. The document set is not just evidence — it is a proxy for how seriously the borrower has approached the whole exercise.

## A practical framework

Documentation discipline generally means:

**Completeness.** The core materials are present — ownership and title, financial information, valuation position, planning or approvals where relevant, and leases or contracts.

**Currency.** Documents are up to date, and the dates are visible.

**Consistency.** The figures and facts across documents agree with each other and with the summary.

**Organisation.** Materials sit in one structured place, easy to navigate, rather than in an email chain.

**Access control.** Sensitive documents are shared deliberately, not forwarded.

Good documentation does not overstate a transaction; it simply lets the transaction be seen clearly.

## Where GP CAP fits

GP CAP assembles the supporting data room, identifies gaps before distribution, and controls access by agreement and role. For the borrower, that turns documentation from a last-minute scramble into an asset that supports a faster, more credible review by suitable counterparties.

${CTA_LINE}`,
  },
  {
    slug: 'how-controlled-deal-rooms-improve-lender-review',
    title: 'How controlled deal rooms improve lender review',
    dek: 'A permissioned deal room is not just secure storage. It changes how efficiently — and how confidently — a capital partner can review a transaction.',
    category: 'capital-partner-education',
    audience: 'lenders',
    readTime: '5 min read',
    date: '2026-06-19',
    author: 'GP Capital',
    seoTitle: 'How controlled deal rooms improve lender review | GP Capital',
    seoDescription:
      'Why a controlled, permissioned deal room improves the quality and pace of capital-partner review in private credit and real estate finance.',
    body: `A deal room is often thought of as a place to store documents. For a capital partner reviewing a transaction, a controlled deal room is more than storage — it is the environment in which the review actually happens, and its design affects the quality of that review.

## Why it happens

Review over email is inefficient and uncertain. Documents arrive in fragments, versions diverge, and a reviewer can never be sure they are looking at the current, complete set. That uncertainty slows the work and tempers confidence in whatever conclusion is reached. The problem is rarely the analysis; it is the disorganised information the analysis has to sit on.

## A practical framework

A controlled deal room improves review when it provides:

**A single current source.** One organised set of materials, so the reviewer is never guessing at versions.

**Permissioned access.** Visibility mapped to role and counterparty status, so disclosure is proportionate.

**Preview and download controls.** The ability to review a document without every party retaining a copy of sensitive material.

**Structured navigation.** Materials organised logically, so a reviewer can find what they need.

**A clear record.** An account of what was shared and with whom, useful during the review and afterwards.

Together these let a capital partner spend their time on judgement rather than on assembling the picture.

## Where GP CAP fits

GP CAP's controlled deal rooms are built for exactly this. A capital partner reviews current, organised information under permissioned access, with the borrower's confidential material protected throughout. The review is faster, the basis for it is clearer, and both sides work from the same record.

${CTA_LINE}`,
  },
  {
    slug: 'understanding-lender-appetite-why-interest-is-not-a-term-sheet',
    title: 'Understanding lender appetite: why interest is not a term sheet',
    dek: 'Early interest is encouraging, but it is not commitment. Understanding the distance between appetite and terms keeps a transaction on realistic footing.',
    category: 'borrower-guidance',
    audience: 'borrowers',
    readTime: '5 min read',
    date: '2026-06-26',
    author: 'GP Capital',
    seoTitle: 'Lender Appetite: Interest vs a Term Sheet | GP Capital',
    seoDescription:
      'Why early capital-partner interest is not the same as a term sheet, and how borrowers can read appetite realistically through a transaction.',
    body: `Early enthusiasm from a capital partner feels like progress, and it can be. But interest and commitment sit some distance apart, and treating the first as the second is a common way for borrowers to be caught out later in a transaction.

## Why it happens

Appetite is expressed early and costs little. A capital partner can be genuinely interested based on a summary, before diligence, credit review and documentation have tested the transaction. Interest reflects a first impression; a term sheet reflects a considered position after review. Because the language of early conversations is warm, it is easy to hear more certainty than has actually been offered.

## A practical framework

A more realistic reading of appetite considers:

**What has it been based on?** Interest formed from a summary is provisional; a view formed after reviewing the full pack is firmer.

**What conditions attach?** Early indications usually carry assumptions that diligence must confirm.

**How specific is it?** General enthusiasm differs from a specific indication of structure, amount and terms.

**What is the next concrete step?** A defined path toward terms signals more than open-ended interest.

Reading appetite well is not cynicism. It is what lets a borrower keep options open and avoid over-committing to a single conversation too early.

## Where GP CAP fits

GP CAP's structured process is designed to keep this distinction clear. Questions, indications and next steps are tracked in one place, so a borrower can see where a conversation genuinely sits rather than relying on tone. Interest is treated as interest, and progress toward terms is visible for what it is.

${CTA_LINE}`,
  },
  {
    slug: 'what-lenders-need-before-reviewing-a-private-credit-opportunity',
    title: 'What lenders need before reviewing a private credit opportunity',
    dek: 'A complete, well-ordered summary gets a faster and more serious read. Here is what experienced capital partners look for first.',
    category: 'capital-partner-education',
    audience: 'lenders',
    readTime: '5 min read',
    date: '2026-06-12',
    author: 'GP Capital',
    seoTitle: 'Private Credit: What Lenders Need to Review | GP Capital',
    seoDescription:
      'The information experienced private-credit lenders and family offices look for first when assessing a real-estate or structured finance opportunity.',
    body: `Capital partners triage quickly. A lender reviewing several opportunities at once will form a first view in minutes, and an incomplete or disorganised pack rarely earns a second look. The aim of a good summary is simple: answer the obvious questions before they are asked.

## The essentials a reviewer expects

**Purpose and use of funds.** What the capital is for, stated plainly — acquisition, construction, bridging, refinance, working capital or recapitalisation. A clear purpose frames everything that follows.

**Amount, structure and term.** The quantum sought, the facility type and position in the capital stack, and the proposed term. A lender needs to know quickly whether the request fits their mandate.

**Security and position.** The asset or assets offered, their type and location, the proposed security rank, and the resulting leverage against value or cost. Where the request sits behind another facility, that should be explicit.

**Valuation status.** Whether a current valuation exists, who prepared it, when, and on what basis. An indicative figure with no support is treated as a starting point, not a fact.

**Planning and approvals.** For development or value-add transactions, the current planning or approval status and any conditions outstanding. This often drives both risk and timing.

**Borrower and sponsor background.** Who is behind the transaction, their relevant track record, and their role in the project. Capital partners lend to people as much as to assets.

**Repayment and exit.** How and when the facility is expected to be repaid — sale, refinance, completion or another defined path. A credible exit is frequently the deciding factor.

**Timeline.** When funding is required and what is driving that. Genuine deadlines and artificial urgency read very differently.

## Why structure matters more than volume

A strong pack is not the largest one. It is the one that lets a reviewer reach a confident preliminary view without chasing missing pieces. Standardising each opportunity into a consistent summary, with a permissioned data room behind it, removes friction on both sides: the lender sees current, organised information, and the borrower's confidential material is only shared with the right counterparties.

That is the discipline GP CAP applies before any opportunity is circulated — so that when a capital partner opens a transaction, the work of understanding it has already been done.`,
  },
  {
    slug: 'how-gp-cap-prepares-a-transaction-for-controlled-distribution',
    title: 'How GP CAP prepares a transaction for controlled distribution',
    dek: 'From intake to execution, every mandate moves through the same structured, permissioned path. Here is what that looks like.',
    category: 'platform-trust',
    audience: 'borrowers',
    readTime: '5 min read',
    date: '2026-06-05',
    author: 'GP Capital',
    seoTitle: 'Preparing Deals for Controlled Distribution | GP Capital',
    seoDescription:
      'How GP CAP standardises, packages and controls a private-capital transaction so it reaches the right capital partners without uncontrolled disclosure.',
    body: `Private-capital transactions often fail on process rather than substance. Information is scattered across email, documents are shared without control, and lenders form views from incomplete pictures. GP CAP is built to remove that, by moving every mandate through the same structured path.

## Intake

An opportunity is captured from the borrower, broker or sponsor: the purpose, the amount sought, the security, the timeline and the relevant background. This is the point at which a transaction becomes a defined mandate rather than an informal enquiry.

## Standardisation and packaging

The mandate is normalised into a consistent summary — facts, capital requirement, security position and structure presented the same way every time. The supporting data room is assembled, and any gaps are identified and addressed before anything is shown to a capital partner. A pack that is consistent and complete is taken more seriously, and reviewed faster.

## NDA-gated access

Nothing confidential moves without control. Access to transaction detail is gated by non-disclosure agreement, role and counterparty status. A capital partner sees a transaction because they are a relevant match and have agreed to the appropriate terms — not because a document was forwarded to them.

## Matched distribution

Rather than broadcasting an opportunity to a list, access is opened to the capital partners for whom the transaction is genuinely a fit. Controlled distribution protects the borrower's position and respects the lender's time.

## Structured responses and execution

Questions, indicative terms and next steps are tracked in one place, with a clear record of what was shared and with whom. As a transaction progresses toward terms, that structure keeps every party working from the same current information through to execution.

## Why it matters

Controlled distribution serves both sides of a transaction. The borrower's confidential information stays confidential and is shared only where it should be. The capital partner receives clean, organised, current detail and a defined process. The result is a transaction that moves with less friction and a clear audit trail behind it.`,
  },
  {
    slug: 'borrower-checklist-before-approaching-private-capital',
    title: "A borrower's checklist before approaching private capital",
    dek: 'Preparation determines how seriously a request is taken. Work through these points before you go to market.',
    category: 'borrower-guidance',
    audience: 'borrowers',
    readTime: '4 min read',
    date: '2026-05-28',
    author: 'GP Capital',
    seoTitle: "A borrower's checklist before approaching private capital | GP Capital",
    seoDescription:
      'A practical checklist for borrowers and sponsors preparing to approach private-credit and real-estate finance providers.',
    body: `The quality of a funding request shapes the response to it. A clear, complete request is engaged with quickly and seriously; a vague one tends to stall. Before approaching private capital, it is worth being able to answer the following plainly.

## Purpose

What, specifically, will the capital fund? "Working capital" or "a project" is rarely enough. A precise purpose lets a lender understand the risk and the timeline.

## Amount and structure

How much is needed, and in what form — senior debt, bridging, construction, mezzanine or another structure. Knowing where your request sits in the capital stack matters as much as the headline figure.

## Security

What asset or assets are offered as security, where they sit, and what position is available. If there is an existing facility ahead of the request, be ready to explain it.

## Valuation

Is there a current valuation? Who prepared it and on what basis? A recent, supportable figure carries far more weight than an estimate.

## Timeline

When are funds required, and what is driving that date? A realistic timeline is more credible — and easier to fund — than an artificial deadline.

## Exit and repayment

How, and when, will the facility be repaid? Sale, refinance or completion are all legitimate, but the path needs to be defined and plausible.

## Documents

Have the core materials ready: title and ownership, financial information, plans or approvals where relevant, leases or contracts, and anything specific to the asset. A request supported by organised documents signals that the rest of the transaction will be handled the same way.

## Expectations

Be realistic about terms. Private capital prices for risk, structure and speed. Engaging with the market on that basis leads to faster, better outcomes than anchoring to terms that do not reflect the transaction.

Preparation is not a formality. It is the single biggest factor in how a request is received — and the work done before going to market is rarely wasted.`,
  },
  {
    slug: 'why-controlled-access-matters-in-private-capital-transactions',
    title: 'Why controlled access matters in private capital transactions',
    dek: 'Discretion and control are not friction in private capital — they are the point. Here is what controlled access protects.',
    category: 'platform-trust',
    audience: 'all',
    readTime: '4 min read',
    date: '2026-05-20',
    author: 'GP Capital',
    seoTitle: 'Controlled Access in Private Capital | GP Capital',
    seoDescription:
      'Why permissioned access, document controls and an audit-aware workflow protect both borrowers and capital partners in private-capital transactions.',
    body: `Many private-capital transactions are still run over email threads and shared links. It works until it does not — and when it fails, it tends to fail in ways that are hard to undo: a confidential document forwarded once too often, two parties working from different versions, and no record of who saw what.

## The problem with uncontrolled distribution

Email and ad-hoc file sharing offer no real control. Once information leaves your hands, you cannot see where it goes, you cannot withdraw it, and you cannot prove what was shared. For confidential transactions involving sensitive borrower and asset information, that is a meaningful exposure for everyone involved.

## What controlled access changes

**Permissioned data rooms.** Information sits in one place, and access is granted deliberately rather than by forwarding. The right parties see the right material, and no one else does.

**Preview and download controls.** There is a difference between letting a counterparty review a document and letting them keep a copy. Controlling that distinction matters for genuinely sensitive material.

**Role-based visibility.** Not every party to a transaction needs to see everything. Access mapped to role and counterparty status keeps disclosure proportionate.

**An audit-aware workflow.** A clear record of what was shared, with whom, and when — useful while a transaction is live, and valuable afterwards.

## Why both sides benefit

For the borrower, controlled access protects confidentiality and keeps a sensitive transaction from circulating beyond where it should. For the capital partner, it means working from clean, current, organised information rather than a forwarded chain of uncertain provenance. For the transaction itself, it creates a defined process and a record behind it.

In private capital, discretion and control are not obstacles to getting a deal done. They are part of doing it properly — and they are built into how GP CAP handles every mandate.`,
  },
];

/** Lookup helpers */
export const getInsightBySlug = (slug) => insights.find((a) => a.slug === slug);
export const getCategoryLabel = (key) =>
  insightCategories.find((c) => c.key === key)?.label || key;
export const getAudienceLabel = (key) =>
  insightAudiences.find((a) => a.key === key)?.label || key;

export default insights;

// Reserved for approved consumer content; do not mix with institutional categories.
export const futureHomeLoanCategory = { key: 'home-loans', label: 'Home Loans' };
